import type { ConnectionProvider } from './connection.js';

/**
 * The canonical tool vocabulary - INT-001, and `docs/architecture/contracts.md`
 * §A.1, which is normative.
 *
 * **Why a canonical layer exists at all.** ADR-0001: every agent-facing tool is
 * ours, and a provider's identifier lives in the binding rather than in the
 * tool. `docs/architecture/pipedream-connect.md` §6.8 states the cost of the
 * alternative - "once prompts and skills start referencing
 * pipedream.some_action_123, migration becomes expensive" - and §6.5 says why
 * MCP's tool schema is not enough on its own: risk, side effect, approval,
 * data classification and audit redaction have nowhere to live in it.
 *
 * So a skill references `crm.contacts.search`, and the gateway decides which
 * provider primitive runs it. Nothing here knows Pipedream exists.
 *
 * These are shapes, not rules. Which capabilities exist is the Tool Registry -
 * INT-002, COW-112, not in this batch - and what an approval means is
 * `packages/domain`.
 */

/**
 * What a tool does to the world, in the order it is written in the blueprint.
 *
 * The ordering is not alphabetical and is not incidental: it runs from the
 * harmless to the irreversible, and `approval-policy-permissions.md` reads it
 * that way. Do not sort it.
 */
export const TOOL_SIDE_EFFECTS = [
  'READ_ONLY',
  'DRAFT',
  'REVERSIBLE_WRITE',
  'EXTERNAL_COMMUNICATION',
  'FINANCIAL',
  'CODE_DEPLOY',
  'DESTRUCTIVE',
  'SECURITY',
] as const;

export type ToolSideEffect = (typeof TOOL_SIDE_EFFECTS)[number];

/**
 * Where a tool's side-effect class came from - ADR-0045.
 *
 * Here rather than beside either reader, because it had two definitions one
 * import apart: `provider_tool`'s CHECK constraint enforces it, the classifier
 * produces it, and a fourth value added to one of them would have compiled
 * against the other and been refused by the database at run time.
 *
 * - `PROVIDER` - read from the provider's own MCP `annotations`. The normal case.
 * - `CURATED` - a person overrode it. Nothing writes this yet.
 * - `UNCLASSIFIED` - the provider said nothing about side effects. **Not a
 *   synonym for a write**: it means nobody has classified this tool, and a
 *   person decides before it runs.
 */
export const TOOL_CLASSIFICATIONS = ['PROVIDER', 'CURATED', 'UNCLASSIFIED'] as const;
export type ToolClassification = (typeof TOOL_CLASSIFICATIONS)[number];

export const TOOL_RISKS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
export type ToolRisk = (typeof TOOL_RISKS)[number];

/**
 * Whether a human has to say yes.
 *
 * `POLICY` and not `SOMETIMES`: the decision belongs to a policy document that
 * can be read and audited, and a tool that says "sometimes" has moved the rule
 * into whoever is reading it.
 */
export const TOOL_APPROVALS = ['NEVER', 'POLICY', 'ALWAYS'] as const;
export type ToolApproval = (typeof TOOL_APPROVALS)[number];

/**
 * A JSON Schema document, kept opaque on purpose.
 *
 * The blueprint's `JsonSchema` is not defined anywhere in it, and inventing a
 * structural type here would be a second, weaker JSON Schema that drifts from
 * the real one. Validation of a tool's arguments happens where the tool runs -
 * INT-015, not this batch - against a real validator. Until then this records
 * that the field holds a schema document and says nothing about its contents.
 */
export type JsonSchema = Readonly<Record<string, unknown>>;

/**
 * How one provider performs one canonical capability.
 *
 * ADR-0001 is the whole point of this type: `pipedream.some_action_123` appears
 * *here* and nowhere else, so migrating off a provider rewrites bindings rather
 * than prompts. `primitive` is the choice `pipedream-connect.md` §6.4 says the
 * gateway must make rather than the agent - a prebuilt action, an MCP call, the
 * proxy, a trigger or a workflow are different things with different costs.
 */
export const PROVIDER_PRIMITIVES = ['ACTION', 'MCP_TOOL', 'PROXY', 'TRIGGER', 'WORKFLOW'] as const;
export type ProviderPrimitive = (typeof PROVIDER_PRIMITIVES)[number];

export interface ProviderBinding {
  /**
   * Which provider. The same union `connection.provider` holds, and not a
   * `string` - this file argues four lines above that a bare string would let a
   * typo through to an approval decision, and ADR-0001 makes this the field the
   * whole canonical layer turns on.
   */
  readonly provider: ConnectionProvider;
  /** The app this binding needs a connection to: `hubspot`, `slack`. */
  readonly appKey: string;
  /** The provider's own identifier for the thing that does the work. */
  readonly providerActionId: string;
  readonly primitive: ProviderPrimitive;
  /**
   * Ranked, because §6.8 forbids "the most recently connected account" as a
   * routing rule and the same argument applies to providers: a fallback that
   * depends on ordering nobody wrote down is not a fallback.
   */
  readonly priority: number;
}

/**
 * A capability an employee can use, independent of who performs it.
 *
 * Field for field with `contracts.md` §A.1. Two are richer than the blueprint's
 * bare `string`: `sideEffect`, `risk` and `approval` are unions rather than
 * strings, because the blueprint writes them as a fixed list and a `string`
 * there would let a typo through to an approval decision.
 */
export interface ToolCapability {
  /** Dotted and stable - `crm.contacts.search`. This is what a skill names. */
  readonly id: string;
  readonly version: number;
  readonly domain: string;
  readonly description: string;
  readonly inputSchema: JsonSchema;
  readonly outputSchema?: JsonSchema;
  readonly sideEffect: ToolSideEffect;
  readonly risk: ToolRisk;
  readonly approval: ToolApproval;
  /** From `data-classification-and-compliance.md`; free-form until DATA-016. */
  readonly dataClasses: readonly string[];
  readonly providerBindings: readonly ProviderBinding[];
}
